import { useCallback, useEffect, useState, type KeyboardEvent } from 'react'
import { api, ApiError, type Inventory, type Warehouse } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, EmptyState, Notice, Select, Spinner } from '../ui/controls'
import ProductPicker from '../ui/ProductPicker'

/**
 * Инвентаризация (п. 4.15).
 *
 * Сетка быстрого ввода: число вводится с клавиатуры, переход по строкам
 * стрелками, расхождения подсвечены. Складские операции во время открытого
 * документа не блокируются, поэтому ожидаемый остаток пересчитывается
 * на лету, а окончательно — в момент проводки.
 */
export default function InventoryPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.stock_adjust

  const [list, setList] = useState<Inventory[] | null>(null)
  const [openID, setOpenID] = useState<number | null>(null)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async () => {
    try {
      setList(await api.inventories())
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' })
      setList([])
    }
  }, [])

  useEffect(() => {
    void reload()
    void api.warehouses().then((w) => setWarehouses(w.filter((x) => x.is_active)))
  }, [reload])

  if (openID !== null) {
    return (
      <InventorySheet
        id={openID}
        canWrite={canWrite}
        onClose={async () => {
          setOpenID(null)
          await reload()
        }}
      />
    )
  }

  if (list === null) return <Spinner label="Загрузка документов…" />

  return (
    <section className="max-w-4xl">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Инвентаризация</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            На одном складе одновременно можно вести только один документ.
          </p>
        </div>
        {canWrite && warehouses.length > 0 && (
          <NewInventory
            warehouses={warehouses}
            onCreated={async (inv) => {
              await reload()
              setOpenID(inv.id)
            }}
            onError={(text) => setNotice({ kind: 'error', text })}
          />
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {list.length === 0 ? (
        <EmptyState
          title="Документов нет"
          hint="Создайте документ, чтобы пересчитать остатки на складе. Продажи во время пересчёта не блокируются."
        />
      ) : (
        <ul className="divide-y divide-ink-200 rounded-lg border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {list.map((inv) => (
            <li key={inv.id}>
              <button
                type="button"
                onClick={() => setOpenID(inv.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-ink-50 dark:hover:bg-ink-900"
              >
                <span className="font-mono text-sm text-ink-700 dark:text-ink-300">
                  {inv.number}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink-600 dark:text-ink-400">
                  {inv.warehouse_name} · позиций {inv.line_count}
                </span>
                <span className="text-xs text-ink-400">{inv.started_at}</span>
                <StateBadge state={inv.state} title={inv.state_title} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function StateBadge({ state, title }: { state: string; title: string }) {
  const cls =
    state === 'posted'
      ? 'bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100'
      : state === 'cancelled'
        ? 'bg-ink-100 text-ink-500 dark:bg-ink-800'
        : 'bg-warning-100 dark:bg-warning-700/25 text-warning-700 dark:text-warning-100'
  return <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`}>{title}</span>
}

function NewInventory({
  warehouses,
  onCreated,
  onError,
}: {
  warehouses: Warehouse[]
  onCreated: (inv: Inventory) => Promise<void>
  onError: (text: string) => void
}) {
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id ?? 0)
  const [pending, setPending] = useState(false)

  return (
    <div className="flex items-end gap-2">
      <label className="block">
        <span className="mb-1 block text-xs text-ink-500">Склад</span>
        <Select value={warehouseId} onChange={(e) => setWarehouseId(Number(e.target.value))}
          className="rounded-md border border-ink-300 bg-white px-2.5 py-2 text-sm dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
        >
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
      </label>
      <Button
        variant="primary"
        size="sm"
        disabled={pending}
        onClick={async () => {
          setPending(true)
          try {
            const inv = await api.createInventory({
              warehouse_id: warehouseId,
              scope: 'warehouse',
            })
            await onCreated(inv)
          } catch (e) {
            onError(e instanceof ApiError ? e.message : 'Не удалось создать документ')
          } finally {
            setPending(false)
          }
        }}
      >
        Начать пересчёт
      </Button>
    </div>
  )
}

/** Сетка быстрого ввода факта. */
function InventorySheet({
  id,
  canWrite,
  onClose,
}: {
  id: number
  canWrite: boolean
  onClose: () => Promise<void>
}) {
  const [inv, setInv] = useState<Inventory | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [confirmPost, setConfirmPost] = useState(false)
  const [pending, setPending] = useState(false)

  const load = useCallback(async () => {
    setInv(await api.inventory(id))
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  if (inv === null) return <Spinner label="Загрузка документа…" />

  const editable = inv.editable && canWrite
  const s = inv.summary

  async function save(productId: number, raw: string) {
    const value = raw.trim() === '' ? null : Number(raw)
    try {
      await api.setInventoryLine(id, { product_id: productId, actual_quantity: value })
      await load()
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось записать' })
    }
  }

  function onCellKey(e: KeyboardEvent<HTMLInputElement>, index: number) {
    // Перемещение по строкам стрелками без обращения к мыши.
    const step = e.key === 'ArrowDown' || e.key === 'Enter' ? 1 : e.key === 'ArrowUp' ? -1 : 0
    if (step === 0) return
    e.preventDefault()
    const next = document.querySelector<HTMLInputElement>(`[data-cell="${index + step}"]`)
    next?.focus()
    next?.select()
  }

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="mb-1 text-xs text-ink-500 hover:text-brand-600"
          >
            ← К перечню документов
          </button>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">
            Инвентаризация {inv.number}
          </h2>
          <p className="mt-0.5 text-sm text-ink-500">
            {inv.warehouse_name} · начата {inv.started_at} · <StateBadge state={inv.state} title={inv.state_title} />
          </p>
        </div>

        {editable && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => setConfirmPost(true)}
              disabled={s.filled === 0 || pending}
            >
              Провести
            </Button>
            <Button
              variant="danger"
              disabled={pending}
              onClick={async () => {
                await api.cancelInventory(id)
                await onClose()
              }}
            >
              Отменить документ
            </Button>
          </div>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Позиций" value={s.total} />
        <Stat label="Заполнено" value={s.filled} />
        <Stat label="Расхождений" value={s.variances} accent={s.variances > 0} />
        <Stat label="Излишек" value={s.surplus} />
        <Stat label="Недостача" value={s.shortage} />
      </div>

      {s.value_diff !== 0 && (
        <p className="mb-4 text-sm text-ink-600 dark:text-ink-400">
          Расхождение в деньгах по цене продажи:{' '}
          <span className={s.value_diff < 0 ? 'text-danger-700 dark:text-danger-100' : 'text-success-700 dark:text-success-100'}>
            {s.value_diff.toLocaleString('ru-RU')} ₸
          </span>
        </p>
      )}

      {editable && (
        <div className="mb-4 max-w-md">
          <span className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300">
            Добавить позицию сканером
          </span>
          <ProductPicker
            warehouseId={inv.warehouse_id}
            placeholder="Товар найден на полке, но его нет в списке"
            onPick={async (item) => {
              await api.setInventoryLine(id, { product_id: item.product_id, actual_quantity: 1 })
              await load()
            }}
          />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
        <table className="w-full min-w-[46rem] text-left text-sm">
          <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
            <tr>
              <th className="px-3 py-2.5 font-medium">Товар</th>
              <th className="px-3 py-2.5 text-right font-medium">Снимок</th>
              <th className="px-3 py-2.5 text-right font-medium">Движения</th>
              <th className="px-3 py-2.5 text-right font-medium">Ожидается</th>
              <th className="px-3 py-2.5 text-right font-medium">Факт</th>
              <th className="px-3 py-2.5 text-right font-medium">Расхождение</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
            {(inv.lines ?? []).map((l, i) => (
              <tr
                key={l.product_id}
                className={l.variance !== null && l.variance !== 0 ? 'bg-warning-100/40' : ''}
              >
                <td className="px-3 py-1.5">
                  <span className="text-ink-800 dark:text-ink-200">{l.product_name}</span>
                  <span className="ml-2 font-mono text-xs text-ink-400">{l.product_sku}</span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-ink-500">
                  {l.snapshot_quantity}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                  {l.movements_since !== 0 ? (
                    <span
                      className="text-warning-700 dark:text-warning-100"
                      title="По позиции были движения после начала документа"
                    >
                      {l.movements_since > 0 ? '+' : ''}
                      {l.movements_since}
                    </span>
                  ) : (
                    <span className="text-ink-300">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-ink-700 dark:text-ink-300">
                  {l.expected}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <input
                    data-cell={i}
                    type="number"
                    defaultValue={l.actual_quantity ?? ''}
                    disabled={!editable}
                    onBlur={(e) => {
                      const raw = e.target.value
                      const current = l.actual_quantity === null ? '' : String(l.actual_quantity)
                      if (raw !== current) void save(l.product_id, raw)
                    }}
                    onKeyDown={(e) => onCellKey(e, i)}
                    placeholder="—"
                    className={`w-20 rounded border px-2 py-1 text-right text-sm tabular-nums ${
                      l.actual_quantity === null
                        ? 'border-dashed border-ink-300 bg-transparent'
                        : 'border-ink-300 bg-white dark:bg-ink-900'
                    } dark:border-ink-700`}
                  />
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                  {l.variance === null ? (
                    <span className="text-ink-300">—</span>
                  ) : l.variance === 0 ? (
                    <span className="text-ink-500">0</span>
                  ) : (
                    <span className={l.variance > 0 ? 'text-success-700 dark:text-success-100' : 'text-danger-700 dark:text-danger-100'}>
                      {l.variance > 0 ? '+' : ''}
                      {l.variance}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmPost && (
        <ConfirmDialog
          title="Провести инвентаризацию?"
          message={
            <>
              Будут созданы корректирующие движения по позициям с расхождением. Ожидаемый
              остаток пересчитывается в момент проводки, поэтому итог может отличаться
              от показанного, если по позициям прошли продажи.
              <br />
              <br />
              Незаполненные позиции ({s.total - s.filled}) не затрагиваются.
            </>
          }
          confirmLabel="Провести"
          pending={pending}
          onCancel={() => setConfirmPost(false)}
          onConfirm={async () => {
            setPending(true)
            try {
              const res = await api.postInventory(id)
              setConfirmPost(false)
              setNotice({
                kind: 'success',
                text: `Проведено. Корректирующих движений: ${res.movements}.`,
              })
              await load()
            } catch (e) {
              setConfirmPost(false)
              setNotice({
                kind: 'error',
                text: e instanceof ApiError ? e.message : 'Не удалось провести',
              })
            } finally {
              setPending(false)
            }
          }}
        />
      )}
    </section>
  )
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-ink-200 px-3 py-2 dark:border-ink-800">
      <div className="text-xs text-ink-500">{label}</div>
      <div
        className={`text-lg font-semibold tabular-nums ${
          accent ? 'text-warning-700 dark:text-warning-100' : 'text-ink-900 dark:text-ink-50'
        }`}
      >
        {value}
      </div>
    </div>
  )
}
